/**
 * Boîte mail intégrée — navigation type Gmail / Apple Mail (iter257).
 *
 * Gauche : onglets de pré-filtrage (attention / clients / factures / OR /
 * autres) + liste des conversations. Droite : lecture du fil et réponse avec
 * les assistants Claude (suggérer, reformuler, traduire, résumer).
 * Sur mobile, les deux volets s'enchaînent (liste → message).
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search, RefreshCw, PenSquare, Send, Paperclip, Mail as MailIcon, X, Sparkles, Loader2,
  ArrowLeft, User, Car,
} from "lucide-react";
import api from "@/lib/api";
import MailThreadList, { MAIL_TABS } from "@/components/mail/MailThreadList";
import MailMessageBody from "@/components/mail/MailMessageBody";
import AttachmentsInput from "@/components/mail/AttachmentsInput";
import MailAssistMenu from "@/components/mail/MailAssistMenu";


const fullDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fr-CH", {
      weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Mailbox() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({});
  const [tab, setTab] = useState("tous");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyBody, setReplyBody] = useState("");
  const [sending, setSending] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [compose, setCompose] = useState({ to: "", subject: "", body_html: "" });
  const [attachments, setAttachments] = useState([]);
  const [assisting, setAssisting] = useState(null);
  const [summary, setSummary] = useState(null);
  const [suggestedFrom, setSuggestedFrom] = useState(null);
  const [clientQuery, setClientQuery] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientSearching, setClientSearching] = useState(false);
  const [view, setView] = useState("list");  // mobile : list | thread
  const reqRef = useRef(0);   // ignore les réponses arrivées dans le désordre
  const replyRef = useRef(null);
  const searchRef = useRef(null);

  const loadThreads = useCallback(async (opts = {}) => {
    const reqId = ++reqRef.current;
    try {
      const { data } = await api.get("/mail/threads", {
        params: { q: q || undefined, categorie: tab, limit: 60 },
      });
      if (reqId !== reqRef.current) return;   // une requête plus récente a pris le relais
      setThreads(data?.items || []);
      setCounts(data?.counts || {});
    } catch {
      if (!opts.silent) toast.error("Impossible de charger la boîte mail");
    } finally {
      setLoading(false);
    }
  }, [q, tab]);

  // iter306 — aucune relève automatique : la liste affiche ce qui est déjà en
  // base, la boîte Infomaniak n'est interrogée que sur clic d'« Actualiser ».
  useEffect(() => {
    loadThreads();
  }, [loadThreads]);

  const openThread = useCallback(async (threadKey) => {
    setSelected(threadKey);
    setView("thread");
    setSummary(null);
    setReplyBody("");
    try {
      const { data } = await api.get(`/mail/threads/${threadKey}`);
      setMessages(data?.messages || []);
      setThreads((prev) => prev.map((t) => (t.thread_key === threadKey ? { ...t, unread: 0 } : t)));
    } catch {
      toast.error("Impossible d'ouvrir cette conversation");
    }
  }, []);

  const toggleSeen = async (t) => {
    const nextSeen = t.unread > 0;
    try {
      await api.post(`/mail/threads/${t.thread_key}/seen`, { seen: nextSeen });
      setThreads((prev) => prev.map((x) => (
        x.thread_key === t.thread_key ? { ...x, unread: nextSeen ? 0 : Math.max(1, x.count || 1) } : x
      )));
    } catch {
      toast.error("Action impossible");
    }
  };

  const forceSync = async () => {
    setLoading(true);
    try {
      await api.post("/mail/sync");
    } catch {
      toast.error("Relève impossible — vérifiez les identifiants dans Paramètres → Mail");
    }
    await loadThreads();
  };

  const sendReply = async () => {
    if (!replyBody.trim() || !selected) return;
    setSending(true);
    try {
      if (suggestedFrom?.messageId && suggestedFrom?.draft && suggestedFrom.draft !== replyBody) {
        api.post(`/mail/messages/${suggestedFrom.messageId}/learn`, { final_text: replyBody })
          .catch(() => { /* apprentissage non critique */ });
      }
      await api.post(`/mail/threads/${selected}/reply`, {
        body_html: replyBody.replace(/\n/g, "<br/>"),
        attachments,
      });
      toast.success("Réponse envoyée");
      setReplyBody("");
      setAttachments([]);
      setSuggestedFrom(null);
      await openThread(selected);
      await loadThreads({ silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const suggestReply = async () => {
    const lastIn = [...messages].reverse().find((m) => m.direction !== "out");
    if (!lastIn) { toast.error("Aucun message reçu à répondre dans ce fil."); return; }
    setAssisting("suggest");
    try {
      const { data } = await api.post(`/mail/messages/${lastIn.id}/suggest-reply`);
      setReplyBody(data?.draft || "");
      setSuggestedFrom({ messageId: lastIn.id, draft: data?.draft || "" });
      toast.success("Brouillon proposé — relisez-le avant d'envoyer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suggestion IA indisponible");
    } finally { setAssisting(null); }
  };

  const assist = async (mode, extra = {}) => {
    if (mode !== "resumer" && !replyBody.trim()) {
      toast.error("Écrivez d'abord un texte à retravailler");
      return;
    }
    setAssisting(mode + (extra.ton || extra.langue || ""));
    try {
      const { data } = await api.post("/mail/assist", {
        mode, text: replyBody, thread_key: selected, ...extra,
      });
      if (mode === "resumer") setSummary(data?.text || "");
      else setReplyBody(data?.text || replyBody);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistant IA indisponible");
    } finally { setAssisting(null); }
  };

  const sendCompose = async () => {
    if (!compose.to.trim() || !compose.subject.trim() || !compose.body_html.trim()) {
      toast.error("Destinataire, objet et message requis");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/mail/compose", {
        ...compose,
        body_html: compose.body_html.replace(/\n/g, "<br/>"),
        attachments,
      });
      toast.success("Message envoyé");
      setComposeOpen(false);
      setCompose({ to: "", subject: "", body_html: "" });
      setAttachments([]);
      setClientQuery("");
      setClientResults([]);
      await loadThreads();
      if (data?.thread_key) openThread(data.thread_key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setSending(false); }
  };

  // Autocomplete client du formulaire « Nouveau message »
  useEffect(() => {
    if (!composeOpen) return;
    const term = clientQuery.trim();
    if (term.length < 2) { setClientResults([]); return; }
    const timer = setTimeout(async () => {
      setClientSearching(true);
      try {
        const { data } = await api.get("/clients", { params: { q: term, limit: 8 } });
        setClientResults((data?.items || data || []).filter((c) => (c.email || "").trim()));
      } catch { setClientResults([]); } finally { setClientSearching(false); }
    }, 250);
    return () => clearTimeout(timer);
  }, [clientQuery, composeOpen]);

  // Raccourcis clavier : j/k naviguer · r répondre · u lu/non lu · / rechercher
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (["input", "textarea", "select"].includes(tag) || e.target.isContentEditable) {
        if (e.key === "Escape") e.target.blur();
        return;
      }
      const idx = threads.findIndex((t) => t.thread_key === selected);
      if (e.key === "j" || e.key === "k") {
        e.preventDefault();
        const next = e.key === "j" ? Math.min(threads.length - 1, idx + 1) : Math.max(0, idx - 1);
        if (threads[next]) openThread(threads[next].thread_key);
      } else if (e.key === "r" && selected) {
        e.preventDefault();
        replyRef.current?.focus();
      } else if (e.key === "u" && threads[idx]) {
        e.preventDefault();
        toggleSeen(threads[idx]);
      } else if (e.key === "/") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, selected, openThread]);

  const currentThread = threads.find((t) => t.thread_key === selected);

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="mailbox-page">
      {/* ─── Colonne conversations ─── */}
      <div className={`${view === "thread" ? "hidden md:flex" : "flex"} w-full md:w-[380px] lg:w-[420px] border-r border-gray-200 flex-col shrink-0 min-w-0`}>
        <div className="p-3 border-b border-gray-200 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
              <MailIcon className="w-4 h-4 text-[#52a675]" /> Boîte mail
            </h2>
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" onClick={forceSync} title="Actualiser" data-testid="mail-refresh-btn">
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => setComposeOpen(true)} title="Nouveau message" data-testid="mail-compose-btn">
                <PenSquare className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
            <Input
              ref={searchRef}
              className="pl-8 h-9"
              placeholder="Rechercher un mail… ( / )"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="mail-search-input"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {MAIL_TABS.map((t) => {
              const n = counts[t.value] || 0;
              const active = tab === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setTab(t.value)}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap border transition-colors ${
                    active
                      ? "bg-[#52a675] text-white border-[#52a675]"
                      : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                  }`}
                  data-testid={`mail-tab-${t.value}`}
                >
                  {t.label}
                  {n > 0 && <span className={`ml-1 ${active ? "text-white/80" : "text-gray-400"}`}>{n}</span>}
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          <MailThreadList
            threads={threads}
            selected={selected}
            onOpen={openThread}
            onToggleSeen={toggleSeen}
            loading={loading}
          />
        </div>
      </div>

      {/* ─── Fil de conversation ─── */}
      <div className={`${view === "list" ? "hidden md:flex" : "flex"} flex-1 flex-col min-w-0`}>
        {!selected ? (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm px-6 text-center">
            Sélectionnez une conversation — raccourcis : j / k naviguer, r répondre, u lu/non lu, / rechercher
          </div>
        ) : (
          <>
            <div className="p-3 border-b border-gray-200">
              <div className="flex items-start gap-2">
                <Button
                  variant="ghost" size="icon"
                  className="md:hidden -ml-2 flex-shrink-0"
                  onClick={() => setView("list")}
                  data-testid="mail-back-btn"
                >
                  <ArrowLeft className="w-4 h-4" />
                </Button>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 text-sm sm:text-base truncate">{currentThread?.subject}</h3>
                  <p className="text-xs text-gray-500 truncate">
                    {currentThread?.last_from_name} · {currentThread?.last_from_addr}
                  </p>
                </div>
                <Button
                  variant="outline" size="sm"
                  onClick={() => assist("resumer")}
                  disabled={assisting === "resumer"}
                  className="flex-shrink-0"
                  title="Résumer ce fil avec l'IA"
                  data-testid="mail-summarize-btn"
                >
                  {assisting === "resumer" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  <span className="hidden sm:inline ml-1.5">Résumer</span>
                </Button>
              </div>
              {/* Contexte CRM détecté */}
              {(currentThread?.client || currentThread?.or_numero) && (
                <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="mail-crm-context">
                  {currentThread.client && (
                    <button
                      type="button"
                      onClick={() => navigate(`/clients/${currentThread.client.id}`)}
                      className="inline-flex items-center gap-1 text-xs text-[#3d8a5c] bg-[#52a675]/10 border border-[#52a675]/30 rounded-full px-2 py-0.5 hover:bg-[#52a675]/20"
                      data-testid="mail-open-client"
                    >
                      <User className="w-3 h-3" /> {currentThread.client.label}
                    </button>
                  )}
                  {currentThread.or_numero && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px]">
                      <Car className="w-3 h-3 mr-1" /> OR {currentThread.or_numero}
                    </Badge>
                  )}
                </div>
              )}
              {summary && (
                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-md p-2 whitespace-pre-wrap text-amber-900" data-testid="mail-summary">
                  {summary}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-3 sm:p-4 bg-gray-50/40">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`mb-3 rounded-lg border p-3 ${
                    m.direction === "out"
                      ? "bg-[#52a675]/5 border-[#52a675]/30 md:ml-10"
                      : "bg-white border-gray-200 md:mr-10"
                  }`}
                  data-testid={`mail-message-${m.id}`}
                >
                  <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-2 pb-2 border-b border-gray-100">
                    <span className="font-medium text-gray-700 truncate">
                      {m.direction === "out" ? "Car Maintenance (vous)" : (m.from_name || m.from_addr)}
                    </span>
                    <span className="flex-shrink-0">{fullDate(m.created_at)}</span>
                  </div>
                  <MailMessageBody html={m.body_html} text={m.body_text} testId={`mail-body-${m.id}`} />
                  {(m.attachments || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {m.attachments.map((a) => (
                        <a
                          key={a.id}
                          href={`/api/mail/attachments/${a.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs flex items-center gap-1 bg-white border border-gray-200 rounded px-2 py-1 hover:bg-gray-50"
                          data-testid={`mail-attachment-${a.id}`}
                        >
                          <Paperclip className="w-3 h-3" /> {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 p-3 space-y-2 bg-white">
              <Textarea
                ref={replyRef}
                placeholder="Votre réponse… ( r )"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                rows={3}
                className="min-h-[76px]"
                data-testid="mail-reply-textarea"
              />
              <AttachmentsInput attachments={attachments} setAttachments={setAttachments} testIdPrefix="reply" />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    onClick={suggestReply}
                    disabled={assisting === "suggest" || !messages.length}
                    title="L'IA rédige un brouillon à partir du dernier message reçu"
                    data-testid="mail-suggest-reply-btn"
                  >
                    {assisting === "suggest" ? <Loader2 className="w-4 h-4 sm:mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 sm:mr-1.5" />}
                    <span className="hidden sm:inline">Suggérer</span>
                  </Button>
                  <MailAssistMenu
                    text={replyBody}
                    threadKey={selected}
                    onResult={setReplyBody}
                    testId="mail-rewrite"
                  />
                </div>
                <Button
                  onClick={sendReply}
                  disabled={sending || !replyBody.trim()}
                  className="bg-[#52a675] hover:bg-[#3d8a5c]"
                  data-testid="mail-reply-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── Nouveau message ─── */}
      <Dialog open={composeOpen} onOpenChange={(v) => { setComposeOpen(v); if (!v) { setClientQuery(""); setClientResults([]); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>Nouveau message</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Label>Rechercher un client</Label>
              <Input
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                placeholder="Nom, prénom ou raison sociale…"
                data-testid="mail-compose-client-search"
              />
              {(clientResults.length > 0 || clientSearching) && (
                <div className="absolute z-10 mt-1 w-full bg-white border rounded shadow max-h-56 overflow-auto">
                  {clientSearching ? (
                    <div className="p-2 text-xs text-gray-500">Recherche…</div>
                  ) : clientResults.map((c) => {
                    const label = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim();
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className="w-full text-left p-2 text-sm hover:bg-gray-50 border-b last:border-b-0"
                        onClick={() => {
                          setCompose((prev) => ({ ...prev, to: c.email }));
                          setClientQuery(label || c.email);
                          setClientResults([]);
                          toast.success(`Destinataire : ${label || c.email}`);
                        }}
                        data-testid={`mail-compose-client-result-${c.id}`}
                      >
                        <div className="font-medium">{label}</div>
                        <div className="text-xs text-gray-500">{c.email}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <Label>À (adresse email)</Label>
              <Input
                value={compose.to}
                onChange={(e) => setCompose((c) => ({ ...c, to: e.target.value }))}
                placeholder="client@exemple.ch"
                data-testid="mail-compose-to"
              />
            </div>
            <div>
              <Label>Objet</Label>
              <Input
                value={compose.subject}
                onChange={(e) => setCompose((c) => ({ ...c, subject: e.target.value }))}
                data-testid="mail-compose-subject"
              />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={6}
                value={compose.body_html}
                onChange={(e) => setCompose((c) => ({ ...c, body_html: e.target.value }))}
                data-testid="mail-compose-body"
              />
            </div>
            <div className="flex items-center gap-2">
              <MailAssistMenu
                text={compose.body_html}
                onResult={(t) => setCompose((c) => ({ ...c, body_html: t }))}
                testId="compose-rewrite"
              />
              <span className="text-[11px] text-gray-400">Reformule ou traduit votre message avant l&apos;envoi</span>
            </div>
            <AttachmentsInput attachments={attachments} setAttachments={setAttachments} testIdPrefix="compose" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={sendCompose} disabled={sending} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="mail-compose-send-btn">
              <Send className="w-4 h-4 mr-2" /> Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
